/**
 * TemplatesManager — "תבניות אימייל" tab of Email Automation.
 *
 * List of email templates with name/tag filters; per template: preview (rendered for a real
 * customer), duplicate, edit, delete. "תבנית חדשה" opens TemplateUpdater in add mode.
 *
 * Data (unchanged): getEmailTemplates · createEmailTemplate (duplicate) · deleteEmailTemplate
 * (after withConfirmAlert). TemplateUpdater / TemplatePreview are the editor and preview dialogs.
 */
import React, { useState, useEffect } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { PlusOutlined, EyeOutlined, CopyOutlined, EditOutlined, DeleteOutlined, SearchOutlined } from '@ant-design/icons';

import * as actionsCreators from './../../../actions/index.js';
import { DataTable, RowActions, EmptyState, Button, Input, StatusBadge, useBreakpoint } from '../../../ui';
import TemplateUpdater from './TemplateUpdater';
import TemplatePreview from './TemplatePreview';
import classes from './TemplatesManager.css';

const TemplatesManager = (props) => {
  const [templates, setTemplates] = useState([]);
  const [showUpdater, setShowUpdater] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [updaterFlag, setUpdaterFlag] = useState('add');
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [previewTemplate, setPreviewTemplate] = useState(null);
  const [nameFilter, setNameFilter] = useState('');
  const [tagFilter, setTagFilter] = useState('');
  const screens = useBreakpoint();

  useEffect(() => {
    loadTemplates();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadTemplates = async () => {
    setLoading(true);
    try {
      const results = await props.getEmailTemplates();
      setTemplates(results);
    } catch (error) {
      console.error('Error loading templates:', error);
      props.showAlert('שגיאה בטעינת תבניות');
    }
    setLoading(false);
  };

  const onOpenUpdater = (flag, template = null) => {
    setUpdaterFlag(flag);
    setSelectedTemplate(template);
    setShowUpdater(true);
  };

  const onCloseUpdater = (shouldRefresh) => {
    setShowUpdater(false);
    setSelectedTemplate(null);
    if (shouldRefresh) loadTemplates();
  };

  const onDeleteTemplate = async (template) => {
    props.withConfirmAlert(async () => {
      try {
        await props.deleteEmailTemplate(template.id);
        props.showAlert('התבנית נמחקה בהצלחה');
        loadTemplates();
      } catch (error) {
        console.error('Error deleting template:', error);
        props.showAlert('שגיאה במחיקת התבנית');
      }
    });
  };

  const onDuplicateTemplate = async (template) => {
    props.withConfirmAlert(async () => {
      try {
        await props.createEmailTemplate({
          name: `עותק של ${template.name}`,
          subject: template.subject,
          tag: template.tag,
          htmlBody: template.htmlBody,
          textBody: template.textBody,
        });
        props.showAlert('התבנית שוכפלה בהצלחה');
        loadTemplates();
      } catch (error) {
        console.error('Error duplicating template:', error);
        props.showAlert('שגיאה בשכפול התבנית');
      }
    }, `לשכפל את התבנית "${template.name}"?`);
  };

  const onShowPreview = (template) => {
    setPreviewTemplate(template);
    setShowPreview(true);
  };

  const filteredTemplates = templates.filter((template) => {
    const matchesName = !nameFilter || template.name.toLowerCase().includes(nameFilter.toLowerCase());
    const matchesTag = !tagFilter || (template.tag && template.tag.toLowerCase().includes(tagFilter.toLowerCase()));
    return matchesName && matchesTag;
  });

  return (
    <div className={classes.tab}>
      {showUpdater && <TemplateUpdater template={selectedTemplate} flag={updaterFlag} onClose={onCloseUpdater} />}
      {showPreview && previewTemplate && (
        <TemplatePreview templateId={previewTemplate.id} templateName={previewTemplate.name} onClose={() => setShowPreview(false)} />
      )}

      <div className={classes.toolbar}>
        <Input
          allowClear
          prefix={<SearchOutlined />}
          placeholder="חפש לפי שם תבנית…"
          value={nameFilter}
          onChange={(e) => setNameFilter(e.target.value)}
        />
        <Input allowClear placeholder="סינון לפי תגית…" value={tagFilter} onChange={(e) => setTagFilter(e.target.value)} />
        <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenUpdater('add')}>
          תבנית חדשה
        </Button>
      </div>

      <DataTable
        data={filteredTemplates}
        loading={loading}
        fit
        onRowClick={(t) => onOpenUpdater('update', t)}
        empty={
          templates.length === 0 ? (
            <EmptyState
              title="אין עדיין תבניות"
              action={
                <Button type="primary" icon={<PlusOutlined />} onClick={() => onOpenUpdater('add')}>
                  תבנית חדשה
                </Button>
              }
            />
          ) : (
            <EmptyState
              title="לא נמצאו תבניות התואמות לסינון"
              action={
                <Button
                  onClick={() => {
                    setNameFilter('');
                    setTagFilter('');
                  }}
                >
                  נקה סינון
                </Button>
              }
            />
          )
        }
        columns={[
          { key: 'name', title: 'שם התבנית', dataIndex: 'name', render: (v) => <span className={classes.name}>{v}</span> },
          {
            key: 'tag',
            title: 'תגית',
            dataIndex: 'tag',
            width: 160,
            responsive: ['md'],
            render: (v) => (v ? <StatusBadge>{v}</StatusBadge> : <span className={classes.muted}>—</span>),
          },
          { key: 'subject', title: 'נושא', dataIndex: 'subject', responsive: ['lg'], ellipsis: true },
          {
            key: 'createdAt',
            title: 'נוצרה',
            dataIndex: 'createdAt',
            width: 110,
            responsive: ['md'],
            render: (v) => <span className="ui-tabular">{new Date(v).toLocaleDateString('he-IL')}</span>,
          },
          {
            key: 'actions',
            title: '',
            align: 'left',
            width: screens.md === false ? 64 : 150, // phones: a single "⋯" menu
            render: (_, template) => (
              <RowActions
                actions={[
                  { key: 'preview', icon: <EyeOutlined />, label: 'תצוגה מקדימה', onClick: () => onShowPreview(template) },
                  { key: 'duplicate', icon: <CopyOutlined />, label: 'שכפול', onClick: () => onDuplicateTemplate(template) },
                  { key: 'edit', icon: <EditOutlined />, label: 'עריכה', onClick: () => onOpenUpdater('update', template) },
                  { key: 'delete', icon: <DeleteOutlined />, label: 'מחיקה', danger: true, onClick: () => onDeleteTemplate(template) },
                ]}
              />
            ),
          },
        ]}
      />
    </div>
  );
};

const mapStateToProps = () => ({});

const mapDispatchToProps = (dispatch) =>
  bindActionCreators(
    {
      getEmailTemplates: actionsCreators.getEmailTemplates,
      createEmailTemplate: actionsCreators.createEmailTemplate,
      deleteEmailTemplate: actionsCreators.deleteEmailTemplate,
      showAlert: actionsCreators.showAlert,
      withConfirmAlert: actionsCreators.withConfirmAlert,
    },
    dispatch
  );

export default connect(mapStateToProps, mapDispatchToProps)(TemplatesManager);
